/**
 * Guided product tour: the pure model (QA F4, founder decision 2026-10-16,
 * reverses BACKLOG D6). No React, no DOM: role -> steps, the step state
 * machine, the auto-start rule and the missing-target fallback all live here
 * so they are unit-tested (frontend/__tests__/guidedTour.test.ts).
 *
 * The renderer is `components/tutorial/TutorialLayer.tsx`; the provider is
 * `context/TutorialContext.tsx`.
 */
import { ROUTES } from '../navigation/routes';

// =================== ROLES ===================

/** Who the tour is written for. Derived from the same flags that gate the nav. */
export type TourRole = 'coach' | 'mentor' | 'participant';

/** The navigation/access flags the shell itself uses (useTabAccess / WebAppShell). */
export interface TourAccessFlags {
  /** Mentees surface in the shell (tier3, or a B2B mentor/admin). */
  showMentorEngagements: boolean;
  /** Progress surface in the shell. */
  showProgress: boolean;
  /** MCS preparation surface (MCS_ENABLED and the mentees surface). */
  showMcs: boolean;
  /** School member (user.school_id set). */
  isB2BUser: boolean;
  /** B2B mentor, school admin or super admin. */
  isMentorOrAdmin: boolean;
  /** Time-boxed school-course access (billing `course_access_active`). */
  courseAccessActive: boolean;
}

export function resolveTourRole(flags: TourAccessFlags): TourRole {
  // Course participants and school members are taught the reduced set they
  // actually have, even when their course grants tier3 (mentees visible).
  if (flags.courseAccessActive || (flags.isB2BUser && !flags.isMentorOrAdmin)) {
    return 'participant';
  }
  if (flags.showMentorEngagements) return 'mentor';
  return 'coach';
}

// =================== STEPS ===================

/** A target candidate is a testID; a trailing `*` matches a testID prefix. */
export type TourTarget = string;

export interface TourStepDef {
  /** Stable id: analytics `step` property and i18n key `guidedTour.steps.<id>`. */
  id: string;
  /** Route to open first. A string route, or a resolver resolved at run time. */
  route?: string;
  dynamicRoute?: 'latest_report' | 'first_engagement';
  /**
   * testIDs to spotlight, tried in order; the first one rendered wins. Steps
   * with no candidates (or none rendered) show as a centered card that says
   * where to go instead (never a broken spotlight).
   */
  targets?: { desktop: TourTarget[]; phone: TourTarget[] };
  /** testID to press before looking for the target (e.g. a workspace tab). */
  prepareClick?: string;
  /** Shown only when the user has this surface. */
  requires?: (flags: TourAccessFlags) => boolean;
}

const both = (...ids: TourTarget[]) => ({ desktop: ids, phone: ids });

/** Every step the tour knows, keyed by id. */
export const TOUR_STEP_LIBRARY: Record<string, TourStepDef> = {
  nav_overview: {
    id: 'nav_overview',
    targets: { desktop: ['shell-sidebar-nav'], phone: ['shell-phone-bar'] },
  },
  upload_session: {
    id: 'upload_session',
    route: ROUTES.TABS_LIBRARY,
    targets: both('library_upload_panel'),
  },
  analysis_runs: {
    id: 'analysis_runs',
    route: ROUTES.TABS_LIBRARY,
    targets: both('shell-nav-sessions'),
  },
  report_open: {
    id: 'report_open',
    dynamicRoute: 'latest_report',
    targets: both('report_overall_level', 'report_header'),
  },
  report_read: {
    id: 'report_read',
    dynamicRoute: 'latest_report',
    targets: both('report_competencies', 'report_summary'),
  },
  chat_reassess: {
    id: 'chat_reassess',
    route: ROUTES.TABS_CHAT,
    targets: both('chat_input', 'chat_workspace_header'),
  },
  progress_goal: {
    id: 'progress_goal',
    route: ROUTES.TABS_PROGRESS,
    targets: both('progress_overview', 'progress_header_actions'),
    requires: (flags) => flags.showProgress,
  },
  profile_plan: {
    id: 'profile_plan',
    route: ROUTES.PROFILE,
    targets: both('profile_plan_credits'),
  },
  profile_account: {
    id: 'profile_account',
    route: ROUTES.PROFILE,
    targets: { desktop: ['shell-nav-profile'], phone: ['shell-nav-profile'] },
  },
  mentees: {
    id: 'mentees',
    route: ROUTES.MENTOR_ENGAGEMENTS,
    // On phones Mentees is in the bottom bar, or under "More" when the bar is full.
    targets: { desktop: ['shell-nav-mentor-engagements'], phone: ['shell-nav-mentor-engagements', 'shell-nav-more'] },
    requires: (flags) => flags.showMentorEngagements,
  },
  create_engagement: {
    id: 'create_engagement',
    route: ROUTES.MENTOR_ENGAGEMENTS,
    targets: both('new_engagement_button'),
    requires: (flags) => flags.showMentorEngagements,
  },
  engagement_add_session: {
    id: 'engagement_add_session',
    dynamicRoute: 'first_engagement',
    prepareClick: 'workspace_tabs_sessions',
    targets: both('engagement_upload_session', 'engagement_sessions_panel'),
    requires: (flags) => flags.showMentorEngagements,
  },
  sof_review: {
    id: 'sof_review',
    dynamicRoute: 'first_engagement',
    prepareClick: 'workspace_tabs_sessions',
    targets: both('engagement_review_session_*', 'engagement_sessions_panel'),
    requires: (flags) => flags.showMentorEngagements,
  },
  crf: {
    id: 'crf',
    dynamicRoute: 'first_engagement',
    prepareClick: 'workspace_tabs_documents',
    targets: both('crf_panel'),
    requires: (flags) => flags.showMentorEngagements,
  },
  send_forms: {
    id: 'send_forms',
    dynamicRoute: 'first_engagement',
    prepareClick: 'workspace_tabs_documents',
    targets: both('crf_email', 'crf_download_pdf', 'crf_panel'),
    requires: (flags) => flags.showMentorEngagements,
  },
  mcs_prep: {
    id: 'mcs_prep',
    route: ROUTES.MCS_PREPARATION,
    targets: {
      desktop: ['shell-nav-mcs-preparation', 'mcs_preparation_intro'],
      phone: ['mcs_preparation_intro'],
    },
    requires: (flags) => flags.showMcs,
  },
  finish: {
    id: 'finish',
    targets: { desktop: ['shell-help'], phone: ['shell-help'] },
  },
};

/** Step order per role. `requires` then drops what the user doesn't have. */
export const TOUR_ROLE_STEPS: Record<TourRole, readonly string[]> = {
  coach: [
    'nav_overview',
    'upload_session',
    'analysis_runs',
    'report_open',
    'report_read',
    'chat_reassess',
    'progress_goal',
    'profile_plan',
    'finish',
  ],
  mentor: [
    'nav_overview',
    'mentees',
    'create_engagement',
    'engagement_add_session',
    'sof_review',
    'crf',
    'send_forms',
    'mcs_prep',
    // Coach basics, in brief.
    'upload_session',
    'report_open',
    'profile_plan',
    'finish',
  ],
  participant: [
    'nav_overview',
    'upload_session',
    'analysis_runs',
    'report_open',
    'chat_reassess',
    'progress_goal',
    'mentees',
    'profile_account',
    'finish',
  ],
};

export function buildTourSteps(role: TourRole, flags: TourAccessFlags): TourStepDef[] {
  return TOUR_ROLE_STEPS[role]
    .map((id) => TOUR_STEP_LIBRARY[id])
    .filter((step): step is TourStepDef => Boolean(step))
    .filter((step) => !step.requires || step.requires(flags));
}

export function targetsFor(step: TourStepDef, isPhone: boolean): TourTarget[] {
  if (!step.targets) return [];
  return isPhone ? step.targets.phone : step.targets.desktop;
}

/**
 * Spotlight when a target was found on screen, otherwise a centered card
 * that tells the user where to go. Never a spotlight without a target.
 */
export function stepPresentation(targetFound: boolean): 'spotlight' | 'card' {
  return targetFound ? 'spotlight' : 'card';
}

// =================== AUTO-START ===================

/** In-app routes the tour may run on (never public, gated, checkout or intake). */
const TOUR_ROUTE_PREFIXES = [
  '/dashboard',
  '/chat',
  '/library',
  '/documents',
  '/students',
  '/progress',
  '/profile',
  '/more',
  '/report',
  '/mentor',
  '/mentor-training',
];

export function isTourRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return TOUR_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export interface AutoStartContext {
  phase: string;
  pathname: string | null | undefined;
  /**
   * `/auth/me` `guided_tour_seen_at`. Only an explicit `null` means "never
   * seen"; `undefined` (field absent: an older backend, or a mocked user)
   * never auto-starts.
   */
  seenAt: string | null | undefined;
  /** The tour already ran (or was skipped) in this app session. */
  handledThisSession: boolean;
  /** A tour is on screen right now. */
  isActive: boolean;
}

export function shouldAutoStart(ctx: AutoStartContext): boolean {
  return (
    ctx.phase === 'authenticated'
    && ctx.seenAt === null
    && !ctx.handledThisSession
    && !ctx.isActive
    && isTourRoute(ctx.pathname)
  );
}

// =================== STATE MACHINE ===================

export type TourSource = 'auto' | 'help' | 'profile';

export type TourState =
  | { status: 'idle' }
  | {
      status: 'running';
      source: TourSource;
      role: TourRole;
      steps: TourStepDef[];
      index: number;
      /** Where the user was when the tour started (returned to on finish). */
      returnTo: string | null;
    }
  | {
      status: 'done';
      outcome: 'completed' | 'skipped';
      source: TourSource;
      role: TourRole;
      /** The step the user was on when the run ended. */
      stepId: string;
      stepIndex: number;
      stepCount: number;
    };

export type TourAction =
  | { type: 'start'; source: TourSource; role: TourRole; steps: TourStepDef[]; returnTo: string | null }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'skip' }
  | { type: 'reset' };

export const INITIAL_TOUR_STATE: TourState = { status: 'idle' };

export function tourReducer(state: TourState, action: TourAction): TourState {
  switch (action.type) {
    case 'start':
      if (state.status === 'running' || action.steps.length === 0) return state;
      return {
        status: 'running',
        source: action.source,
        role: action.role,
        steps: action.steps,
        index: 0,
        returnTo: action.returnTo,
      };
    case 'next':
      if (state.status !== 'running') return state;
      if (state.index >= state.steps.length - 1) {
        return {
          status: 'done',
          outcome: 'completed',
          source: state.source,
          role: state.role,
          stepId: state.steps[state.index].id,
          stepIndex: state.index,
          stepCount: state.steps.length,
        };
      }
      return { ...state, index: state.index + 1 };
    case 'back':
      if (state.status !== 'running' || state.index === 0) return state;
      return { ...state, index: state.index - 1 };
    case 'skip':
      if (state.status !== 'running') return state;
      return {
        status: 'done',
        outcome: 'skipped',
        source: state.source,
        role: state.role,
        stepId: state.steps[state.index].id,
        stepIndex: state.index,
        stepCount: state.steps.length,
      };
    case 'reset':
      return INITIAL_TOUR_STATE;
    default:
      return state;
  }
}

export function currentTourStep(state: TourState): TourStepDef | null {
  return state.status === 'running' ? state.steps[state.index] ?? null : null;
}

/**
 * Whether finishing this run must be written to the server. Every finish or
 * skip is written (the server keeps the first timestamp), so a replay on a
 * device whose `/auth/me` was stale still ends the auto-start everywhere.
 */
export function shouldPersistSeen(state: TourState): boolean {
  return state.status === 'done';
}

// =================== HINTS ===================

/** Contextual "what is this?" hints. Mirrors GUIDANCE_HINT_IDS in backend auth.py. */
export const GUIDANCE_HINT_IDS = [
  'analysis_options',
  'credits_minutes',
  'report_level',
  'sof_review',
  'crf_form',
] as const;
export type GuidanceHintId = typeof GUIDANCE_HINT_IDS[number];

export function isHintVisible(
  hintId: GuidanceHintId,
  serverDismissed: readonly string[] | null | undefined,
  localDismissed: readonly string[],
): boolean {
  return !(serverDismissed ?? []).includes(hintId) && !localDismissed.includes(hintId);
}

// =================== CARD PLACEMENT ===================

export interface TourRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TourCardPlacement {
  left: number;
  top: number;
  width: number;
}

const EDGE = 12;
const GAP = 14;

/**
 * Where the step card goes. Phones dock it to the bottom edge, or to the top
 * when the target sits in the lower half (e.g. the bottom bar). Desktop puts
 * it below, above, right or left of the target, whichever fits first; with
 * no target the card is centered.
 */
export function placeTourCard(
  target: TourRect | null,
  viewport: { width: number; height: number },
  cardHeight: number,
  isPhone: boolean,
): TourCardPlacement {
  const width = isPhone
    ? Math.min(viewport.width - EDGE * 2, 520)
    : Math.min(380, viewport.width - EDGE * 2);
  const clampLeft = (left: number) => Math.max(EDGE, Math.min(left, viewport.width - width - EDGE));
  const clampTop = (top: number) => Math.max(EDGE, Math.min(top, viewport.height - cardHeight - EDGE));
  const centeredLeft = (viewport.width - width) / 2;

  if (!target) {
    return { left: clampLeft(centeredLeft), top: clampTop((viewport.height - cardHeight) / 2), width };
  }

  if (isPhone) {
    const targetCenter = target.y + target.height / 2;
    const top = targetCenter > viewport.height / 2 ? EDGE : viewport.height - cardHeight - EDGE;
    return { left: clampLeft(centeredLeft), top: clampTop(top), width };
  }

  const right = target.x + target.width + GAP;
  // A left-rail target (the sidebar menu) reads best with the card beside it.
  const isLeftRail = target.x + target.width <= viewport.width * 0.3;
  if (isLeftRail && right + width <= viewport.width - EDGE) {
    return { left: right, top: clampTop(target.y), width };
  }
  const alignedLeft = clampLeft(target.x + target.width / 2 - width / 2);
  const below = target.y + target.height + GAP;
  if (below + cardHeight <= viewport.height - EDGE) return { left: alignedLeft, top: below, width };
  const above = target.y - GAP - cardHeight;
  if (above >= EDGE) return { left: alignedLeft, top: above, width };
  if (right + width <= viewport.width - EDGE) return { left: right, top: clampTop(target.y), width };
  const left = target.x - GAP - width;
  if (left >= EDGE) return { left, top: clampTop(target.y), width };
  return { left: clampLeft(centeredLeft), top: clampTop(viewport.height - cardHeight - EDGE), width };
}
